import {
  productFamilyIdFormat,
  productIdFormat,
  supplierIdFormat,
} from "@redotech/redo-model/ids";
import { AdvancedFilterDataSchema } from "@redotech/redo-model/views/views";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";

const forecastingProductSupplierSchema_ = z.object({
  id: supplierIdFormat.schema,
  name: z.string(),
});

export const ForecastingProductSupplierSchema: z.ZodType<ForecastingProductSupplier> =
  forecastingProductSupplierSchema_;
export interface ForecastingProductSupplier
  extends z.infer<typeof forecastingProductSupplierSchema_> {}

const missingReorderParamSchema = createEnum([
  "leadTime",
  "coverageDays",
  "forecast",
  "available",
]);
export type MissingReorderParam = z.infer<typeof missingReorderParamSchema>;

const forecastingProductSchema_ = z.object({
  id: productIdFormat.schema,
  productFamilyId: productFamilyIdFormat.schema,
  image: z.string().nullish(),
  title: z.string(),
  sku: z.string().nullish(),
  vendor: z.string().nullish(),
  tags: z.array(z.string()),
  stockoutDate: zExt.plainDate().nullish(),
  stockoutDateCapped: z.boolean().optional(),
  totalQuantity: z.number(),
  totalAvailable: z.number(),
  totalCommitted: z.number(),
  totalReserved: z.number(),
  moq: z.number().nullish(),
  leadTime: z.number().nullish(),
  buffer: z.number().nullish(),
  coverageDays: z.number().nullish(),
  suppliers: z.array(ForecastingProductSupplierSchema),
  // Reorder calculation fields
  reorderPoint: z.number().nullish(),
  daysUntilReorder: z.number().nullish(),
  unitsToOrder: z.number().nullish(),
  missingReorderParams: z.array(missingReorderParamSchema).optional(),
  // Purchase order info
  purchaseOrderCount: z.number().int(),
  // Incoming shipment info
  incomingShipmentCount: z.number().int(),
  incomingUnits: z.number().int(),
  // Inventory value
  inventoryValue: z.number().nullish(),
  inventoryValueCurrency: z.string().nullish(),
  // Historical sales (past X days)
  sold30Days: z.number().nullish(),
  sold60Days: z.number().nullish(),
  sold90Days: z.number().nullish(),
  sold180Days: z.number().nullish(),
  sold365Days: z.number().nullish(),
  // Forecasted sales (next X days)
  forecast30Days: z.number().nullish(),
  forecast60Days: z.number().nullish(),
  forecast90Days: z.number().nullish(),
  forecast180Days: z.number().nullish(),
  forecast365Days: z.number().nullish(),
  // Run rates (units/day based on historical sales)
  runRate30Days: z.number().nullish(),
  runRate60Days: z.number().nullish(),
  runRate90Days: z.number().nullish(),
  runRate180Days: z.number().nullish(),
  runRate365Days: z.number().nullish(),
});

export const ForecastingProductSchema: z.ZodType<ForecastingProduct> =
  forecastingProductSchema_;
export interface ForecastingProduct
  extends z.infer<typeof forecastingProductSchema_> {}

export const getForecastingProducts = {
  name: "getForecastingProducts" as const,
  input: z.object({
    today: zExt.plainDate(),
    page: z.number().int().min(0).default(0),
    pageSize: z.number().int(),
    search: z.string().optional(),
    sortBy: createEnum([
      "title",
      "sku",
      "vendor",
      "daysOfStockRemaining",
      "totalAvailable",
      "totalQuantity",
      "totalCommitted",
      "totalReserved",
      "moq",
      "leadTime",
      "sold30Days",
      "sold60Days",
      "sold90Days",
      "sold180Days",
      "sold365Days",
      "forecast30Days",
      "forecast60Days",
      "forecast90Days",
      "forecast180Days",
      "forecast365Days",
      "daysUntilReorder",
      "unitsToOrder",
    ]).optional(),
    sortDirection: createEnum(["asc", "desc"]).optional(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({ products: z.array(ForecastingProductSchema) }),
};
